import {
    createPagePagingActions,
    SortDirection,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
} from "@comet/admin";
import { HourglassEmptyOutlined, Refresh } from "@mui/icons-material";
import { Button, Grid, Typography } from "@mui/material";
import { ContentWrapper, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { StyledFilter, StyledLink, TableFilter } from "@src/components/table/TableStyles";
import {
    type GQLCameraBulkCommandsAdminQuery,
    type GQLCameraBulkCommandsAdminQueryVariables,
    GQLCameraBulkCommandStatus,
} from "@src/graphql.generated";
import Incoming from "@src/icons/Incoming";
import Warning from "@src/icons/Warning";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { cameraBulkCommandsQuery } from "./Table.gql";

interface TableProps {
    persistedStateId?: string;
}

const Table: FC<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const sortApi = useTableQuerySort({ columnName: "createdAt", direction: SortDirection.DESC }, { persistedStateId });
    const filterApi = useTableQueryFilter<GQLCameraBulkCommandsAdminQueryVariables>({}, { pagingApi, persistedStateId });
    const intl = useIntl();

    const { status } = filterApi.current;
    const addAdditionalQueryVariablesFn = useCallback(() => ({ status }), [status]);

    const memoizedVariables = useMemoizedTableVariables<GQLCameraBulkCommandsAdminQueryVariables>({
        pagingApi,
        sortApi,
        addAdditionalQueryVariablesFn,
    });

    const { tableData, loading, error, api, refetch } = useTableQuery<GQLCameraBulkCommandsAdminQuery, GQLCameraBulkCommandsAdminQueryVariables>()(
        cameraBulkCommandsQuery,
        {
            resolveTableData: ({ cameraBulkCommandsAdmin }) => ({
                data: cameraBulkCommandsAdmin.nodes,
                totalCount: cameraBulkCommandsAdmin.totalCount,
                pagingInfo: createPagePagingActions(pagingApi, cameraBulkCommandsAdmin),
            }),
            variables: memoizedVariables,
        },
    );

    const camerasBulkCommandStatusOptions = [
        {
            label: intl.formatMessage({ id: "reviermanager.bulkSMS.table.status.pending", defaultMessage: "Ausstehend" }),
            value: GQLCameraBulkCommandStatus.Pending,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.bulkSMS.table.status.finished", defaultMessage: "Erledigt" }),
            value: GQLCameraBulkCommandStatus.Finished,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.bulkSMS.table.status.failed", defaultMessage: "Fehlgeschlagen" }),
            value: GQLCameraBulkCommandStatus.Failed,
        },
    ];

    return (
        <ContentWrapper
            marginVariant="table"
            title={intl.formatMessage({ id: "reviermanager.page.bulkSMS.Protokoll", defaultMessage: "Protokoll" })}
            link={
                <Button color="primary" variant="text" onClick={() => refetch()} startIcon={<Refresh />}>
                    <FormattedMessage id="reviermanager.camera.activity.refetch" defaultMessage="Aktualisieren" />
                </Button>
            }
        >
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={2}>
                        <StyledFilter>
                            <StaticSelectField
                                name="status"
                                label={intl.formatMessage({ id: "reviermanager.bulkSMS.filter.status", defaultMessage: "Status" })}
                                options={camerasBulkCommandStatusOptions}
                                variant="vertical"
                                isClearable
                            />
                        </StyledFilter>
                    </Grid>
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        {...tableData}
                        sortApi={sortApi}
                        columns={[
                            {
                                name: "createdAt",
                                header: <FormattedMessage id="reviermanager.camera.name" defaultMessage="Name" />,
                                render: ({ camera: { name, id } }) => <StyledLink to={`/devices/${id}/edit`}>{name}</StyledLink>,
                            },
                            {
                                name: "createdAt",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.bulkSMS.table.createdAt", defaultMessage: "erstellt am" }),
                                render: ({ createdAt }) => <FormattedDateWidthDateTime value={createdAt} />,
                            },
                            {
                                name: "status",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.bulkSMS.table.status", defaultMessage: "Status" }),
                                render: ({ status }) => {
                                    if (status === GQLCameraBulkCommandStatus.Finished) {
                                        return <Incoming />;
                                    }
                                    if (status === GQLCameraBulkCommandStatus.Failed) {
                                        return <Warning />;
                                    }
                                    return <HourglassEmptyOutlined fontSize="medium" color="primary" />;
                                },
                            },
                            {
                                name: "sentAt",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.bulkSMS.table.sentAt", defaultMessage: "gesendet am" }),
                                render: ({ sentAt }) => sentAt && <FormattedDateWidthDateTime value={sentAt} />,
                            },
                            {
                                name: "finishedAt",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.bulkSMS.table.finishedAt", defaultMessage: "erledigt am" }),
                                render: ({ finishedAt }) => finishedAt && <FormattedDateWidthDateTime value={finishedAt} />,
                            },
                            {
                                name: "executedBy",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.bulkSMS.table.executedBy", defaultMessage: "Absender" }),
                                render: ({ executedBy }) => <Typography>{executedBy}</Typography>,
                            },
                        ]}
                    />
                )}
            </TableQuery>
        </ContentWrapper>
    );
};

export default Table;
